/**
 * Playing with thumbs.
 *
 * The game was built for a keyboard and a mouse, and the two things it asks
 * for that a phone has no obvious answer to are a *held* jump -- the pounce
 * charges while Space is down and launches on release -- and a *held* claw,
 * which is the only way up a fence.  Both are buttons here rather than
 * gestures, because a gesture that has to be held for 0.62 s while the other
 * thumb steers is not a gesture, it is a button with extra steps.
 *
 * The layout is the one every third-person phone game has converged on, and
 * for good reasons worth writing down:
 *
 *   - The stick FLOATS.  It appears wherever the left thumb lands rather than
 *     sitting at a fixed spot, because a thumb cannot see and a fixed stick
 *     means every touch begins with a small miss and a correction.  The
 *     visual is drawn at the touch, so the stick is always exactly under the
 *     thumb that made it.
 *   - Look is a bare drag on the right, with no widget at all.  There is
 *     nothing to hit and nothing to miss.
 *   - The buttons are bottom-right, thumb-sized (56-76 px against the 44 px
 *     that Apple and Google both call the minimum), and stacked so the one
 *     used most -- jump -- is nearest the thumb's rest position.
 *
 * Touches are routed by WHERE THEY START and keep that role until they lift,
 * which is what lets a stick, a look drag and a button press all be live at
 * once.  A finger that begins on the left is a stick for its whole life even
 * if it slides to the right of the screen; anything else would hand the
 * camera to a thumb that was walking.
 */

/** Below this fraction of the width, a new touch is the stick.  Not 0.5: the
    buttons live on the right and the stick wants the bigger half of what is
    left, and a right-handed player's look drags start further right than the
    middle anyway. */
const STICK_SIDE = 0.45;

/**
 * The stick's radius in CSS pixels, and its dead zone.
 *
 * 52 px is a comfortable thumb sweep without moving the hand -- the thumb's
 * arc from a phone-edge rest position is about 15 mm before the wrist has to
 * follow, and 52 px is close to that on every phone DPR the viewport meta
 * normalises to.  The dead zone is a tenth of it: a thumb resting on glass
 * drifts a pixel or two, and without a dead zone a cat set down on a table
 * walks away.  Deflection past the zone is rescaled to the full 0..1, so the
 * zone costs reach rather than resolution.
 */
const STICK_R = 52;
const DEAD = 0.10;

/**
 * Where the stick starts asking for a run.
 *
 * player.js reads the stick as a magnitude, so anything under this is a
 * proportional walk -- a quarter tilt is a creep at 0.155 m/s, half is 0.31.
 * Past the threshold `sprint` goes high and the same magnitude buys the run
 * gait instead, which is 2.4 m/s at full tilt.  The gap at the crossing is
 * real (0.52 m/s to 2.16) and it is the same gap the Shift key has, smoothed
 * by the same ACCEL; hysteresis is what stops a thumb parked exactly on the
 * line flickering between the two several times a second.
 */
const SPRINT_ON = 0.86;
const SPRINT_OFF = 0.76;

/**
 * Look sensitivity, radians per CSS pixel.
 *
 * The mouse uses 0.0026 yaw / 0.0021 pitch, tuned for a hand that can travel
 * the width of a desk.  A thumb travels perhaps 200 px before it has to lift
 * and re-plant, and one such sweep should be worth most of a half turn or the
 * whole pitch range -- so this is roughly 2.5x the mouse.  Pitch keeps the
 * mouse's sign: dragging down looks down, the same direction the desktop
 * drag-look has always gone, so the two do not have to be learned separately.
 */
const LOOK_X = 0.0065;
const LOOK_Y = 0.0052;

/** Metres of camera dolly per pixel of pinch.  Two thumbs closing 100 px pull
    the camera in about a metre, which spans the 0.9..5.0 clamp in a couple of
    comfortable pinches. */
const PINCH = 0.010;

export class TouchControls {
  /**
   * @param canvas  the render surface; stick and look touches land here
   * @param root    the #touch overlay: pointer-events none, buttons inside
   * @param input   the shared input struct main.js hands to player.update
   * @param hooks   { begin(), look(dx,dy), zoom(d), jumpDown(), jumpUp(),
   *                  meow(), say(), blocked() -> bool }
   */
  constructor({ canvas, root, input, hooks = {} }) {
    this.canvas = canvas;
    this.root = root;
    this.input = input;
    this.hooks = hooks;
    this.active = false;
    this.sprinting = false;
    /**
     * When a finger last did anything.
     *
     * preventDefault on every touch should stop the browser synthesising
     * mouse events, but "should" is doing work there -- the compatibility
     * path varies by browser and by whether the touch was consumed -- and a
     * ghost mousedown on the canvas is read by main.js as the claws coming
     * out.  main.js ignores mouse events that arrive within a moment of a
     * touch, which is the standard ghost-click guard, and this is the clock
     * it reads.  A window rather than a permanent flag so a hybrid laptop
     * that was touched once does not lose its mouse for the session.
     */
    this.lastTouch = 0;

    /** id -> { role, ... }.  A touch keeps the role it started with. */
    this.touches = new Map();

    this.stickEl = root?.querySelector("#stick") ?? null;
    this.knobEl = root?.querySelector("#stick-knob") ?? null;
    this.jumpEl = root?.querySelector('[data-touch="jump"]') ?? null;

    // Non-passive on purpose, all four: preventDefault on a touch is what
    // stops the browser synthesising a mouse event ~300 ms later.  Without it
    // every tap on the canvas arrives a second time as a mousedown, and
    // main.js reads a canvas mousedown as the claws coming out -- so a cat
    // walked with the left thumb would flex its claws at nothing, constantly.
    const opts = { passive: false };
    canvas.addEventListener("touchstart", (e) => this.onStart(e), opts);
    canvas.addEventListener("touchmove", (e) => this.onMove(e), opts);
    canvas.addEventListener("touchend", (e) => this.onEnd(e), opts);
    canvas.addEventListener("touchcancel", (e) => this.onEnd(e), opts);

    for (const el of root?.querySelectorAll("[data-touch]") ?? []) {
      this.wireButton(el, el.dataset.touch);
    }

    // A device that says it has a coarse pointer gets the controls before it
    // is touched; everything else waits, and reveals on the first touch.
    if (wantsTouch()) this.reveal();
  }

  /**
   * A held button.
   *
   * Listeners go on the button itself rather than on a shared root with a hit
   * test, because the touch events for a finger are RETARGETED to the element
   * it started on for the whole of its life -- so a thumb that slides off the
   * edge of the jump button still delivers its touchend here, and the jump
   * still releases.  A hit test would have dropped it and left the cat
   * gathering a pounce for ever.
   */
  wireButton(el, name) {
    const opts = { passive: false };
    let id = null;
    const down = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.hooks.blocked?.()) return;
      this.wake();
      if (id !== null) return;                 // already held by another finger
      id = e.changedTouches[0].identifier;
      el.classList.add("on");
      this.press(name, true);
    };
    const up = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (id === null) return;
      // Only the finger that started the press may end it.
      for (const t of e.changedTouches) {
        if (t.identifier !== id) continue;
        id = null;
        el.classList.remove("on");
        this.press(name, false);
        return;
      }
    };
    el.addEventListener("touchstart", down, opts);
    el.addEventListener("touchend", up, opts);
    el.addEventListener("touchcancel", up, opts);
  }

  /** One button's press or release, as the input struct sees it. */
  press(name, down) {
    if (name === "jump") {
      // Straight through to main.js's held/pressed latch rather than to
      // input.jump: the pounce is release-triggered, and the latch is what
      // guarantees a tap is seen high for at least one whole frame.
      if (down) this.hooks.jumpDown?.();
      else this.hooks.jumpUp?.();
    } else if (name === "claw") {
      this.input.claw = down ? 1 : 0;
    } else if (name === "sit") {
      // One-shot: player.js consumes it and the frame loop clears it.
      if (down) this.input.sitToggle = 1;
    } else if (name === "meow") {
      // One-shot too, but straight to the hook rather than through the
      // input struct: the meow is presentation (audio plus a chin-lift),
      // not simulation, and player.js has no business hearing about it.
      // Fire-on-press with nothing latched, so releaseAll() owes it nothing.
      if (down) this.hooks.meow?.();
    } else if (name === "say") {
      // One-shot on the press, like sit: the quick-chat menu is a toggle,
      // and holding a menu open-er means nothing.  Routed through a hook
      // rather than the input struct because the menu is a DOM affair
      // (chat.js), not something player.update has any business seeing.
      if (down) this.hooks.say?.();
    }
  }

  /**
   * Put the layer on screen.
   *
   * Called at construction on a coarse-pointer device, and on the first real
   * touch anywhere else -- and the two have to be separate from starting the
   * game, which is why this is not just the top of wake().
   *
   * Revealing before any touch is not cosmetic.  `.touch` is what hides the
   * keyboard legend, and #keys is a panel that takes pointer events: on a
   * phone held landscape (839 CSS px, so past the 640 px width query that
   * used to be the only thing hiding it) it covers the whole left half of the
   * screen.  Waiting for a first touch to reveal the controls meant waiting
   * for a touch that the legend itself was swallowing -- the stick never
   * appeared, and the game never started.
   */
  reveal() {
    if (this.active) return;
    this.active = true;
    document.documentElement.classList.add("touch");
  }

  /** First touch anywhere: reveal the controls and start the game. */
  wake() {
    this.lastTouch = performance.now();
    this.reveal();
    this.hooks.begin?.();
  }

  onStart(e) {
    this.lastTouch = performance.now();
    e.preventDefault();
    this.wake();
    if (this.hooks.blocked?.()) return;
    for (const t of e.changedTouches) {
      const left = t.clientX < innerWidth * STICK_SIDE;
      // One stick at a time.  A second left-hand finger would otherwise take
      // the stick over mid-stride and snap the cat to a new heading.
      const role = left && !this.hasRole("stick") ? "stick" : "look";
      if (role === "stick") {
        this.touches.set(t.identifier, { role, ox: t.clientX, oy: t.clientY });
        this.showStick(t.clientX, t.clientY, 0, 0);
      } else {
        this.touches.set(t.identifier, { role, x: t.clientX, y: t.clientY });
      }
    }
  }

  onMove(e) {
    this.lastTouch = performance.now();
    e.preventDefault();
    if (this.hooks.blocked?.()) return;
    for (const t of e.changedTouches) {
      const s = this.touches.get(t.identifier);
      if (!s) continue;
      if (s.role === "stick") {
        this.driveStick(t.clientX - s.ox, t.clientY - s.oy, s.ox, s.oy);
      } else {
        this.hooks.look?.(t.clientX - s.x, t.clientY - s.y);
        s.x = t.clientX;
        s.y = t.clientY;
      }
    }
    this.pinch();
  }

  onEnd(e) {
    this.lastTouch = performance.now();
    e.preventDefault();
    for (const t of e.changedTouches) {
      const s = this.touches.get(t.identifier);
      this.touches.delete(t.identifier);
      if (s?.role === "stick") this.releaseStick();
    }
    this.lastPinch = this.touches.size >= 2 ? this.lastPinch : null;
  }

  hasRole(role) {
    for (const s of this.touches.values()) if (s.role === role) return true;
    return false;
  }

  /**
   * Two look fingers dolly the camera.
   *
   * Only look fingers: a pinch measured between the stick thumb and a look
   * thumb would zoom every time the cat changed direction, which is most of
   * the time.  The distance is remembered rather than the start, so a pinch
   * that begins after one finger has already been down still tracks smoothly.
   */
  pinch() {
    const pts = [];
    for (const s of this.touches.values()) if (s.role === "look") pts.push(s);
    if (pts.length < 2) { this.lastPinch = null; return; }
    const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    if (this.lastPinch !== null) this.hooks.zoom?.(-(d - this.lastPinch) * PINCH);
    this.lastPinch = d;
  }

  /** Stick deflection -> the four direction magnitudes and `sprint`. */
  driveStick(dx, dy, ox, oy) {
    const len = Math.hypot(dx, dy);
    const clamped = Math.min(len, STICK_R);
    let m = clamped / STICK_R;
    // Rescale past the dead zone so the whole remaining sweep is usable --
    // subtracting it without rescaling would cap the stick at 0.90 and the
    // cat could never quite reach its own top speed.
    m = m <= DEAD ? 0 : (m - DEAD) / (1 - DEAD);
    const ux = len > 0 ? dx / len : 0;
    const uy = len > 0 ? dy / len : 0;

    const nx = ux * m;
    const ny = uy * m;
    const input = this.input;
    // Screen up is away from the camera, which is what `forward` means.
    input.forward = Math.max(0, -ny);
    input.back = Math.max(0, ny);
    input.right = Math.max(0, nx);
    input.left = Math.max(0, -nx);

    if (this.sprinting ? m < SPRINT_OFF : m > SPRINT_ON) this.sprinting = !this.sprinting;
    input.sprint = this.sprinting ? 1 : 0;

    this.showStick(ox, oy, ux * clamped, uy * clamped);
    this.stickEl?.classList.toggle("run", this.sprinting);
  }

  releaseStick() {
    const input = this.input;
    input.forward = input.back = input.left = input.right = 0;
    input.sprint = 0;
    this.sprinting = false;
    this.stickEl?.classList.remove("shown", "run");
  }

  /** Position the ring at the origin and the knob at the deflection. */
  showStick(ox, oy, kx, ky) {
    if (!this.stickEl) return;
    this.stickEl.style.transform = `translate(${ox}px, ${oy}px)`;
    if (this.knobEl) this.knobEl.style.transform = `translate(${kx}px, ${ky}px)`;
    this.stickEl.classList.add("shown");
  }

  /**
   * Mirror the pounce onto the jump button.
   *
   * The bottom-centre #charge meter is a desktop affordance: on a phone the
   * player's eyes are on the thumb that is doing the gathering, so the button
   * itself fills.  Quantised to hundredths for the same reason ChargeMeter
   * quantises -- a held full charge should stop writing to the DOM entirely.
   */
  setCharge(charge, gathering) {
    if (!this.jumpEl) return;
    const step = gathering ? Math.min(100, Math.round(charge * 100)) / 100 : 0;
    if (step !== this.shownCharge) {
      this.shownCharge = step;
      this.jumpEl.style.setProperty("--charge", step);
    }
    const ready = gathering && step >= 1;
    if (ready !== this.chargeReady) {
      this.chargeReady = ready;
      this.jumpEl.classList.toggle("ready", ready);
    }
  }

  /** Panels take the screen; let go of everything rather than leaving a
      thumb's worth of input latched under a dialog. */
  releaseAll() {
    this.touches.clear();
    this.releaseStick();
    this.input.claw = 0;
    this.hooks.jumpUp?.();
    for (const el of this.root?.querySelectorAll("[data-touch]") ?? []) {
      el.classList.remove("on");
    }
  }
}

/**
 * Is this a device that wants thumb controls?
 *
 * `pointer: coarse` asks the browser rather than guessing from a user-agent
 * string, and it is right about phones and tablets and right about a laptop
 * with a touchscreen and a trackpad (which reports `fine` and gets the
 * keyboard).  It is only the FIRST guess: the layer reveals itself on the
 * first real touch either way, so a device this returns false for still gets
 * controls the moment somebody puts a finger on it.
 */
export function wantsTouch() {
  try {
    return matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}
